import axios from 'axios';
import * as Sentry from '@sentry/react';

export const PROPERTY_ID = process.env.REACT_APP_PROPERTYID || "0196b165-b92f-74cc-ab9e-075e47f993a2";

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

export const PRICING_UNAVAILABLE_MESSAGE = 'Pricing is currently unavailable. Please try again later.';
export const isPricingUnavailable = (error) =>
    error?.response?.data?.code === 'PRICING_CONFIGURATION_UNAVAILABLE'
    || error?.code === 'PRICING_CONFIGURATION_UNAVAILABLE';

export const normalizeApiError = async (error) => {
    let data = error?.response?.data;
    try {
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
            data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsText(data);
            });
        } else if (data instanceof ArrayBuffer) {
            data = new TextDecoder().decode(data);
        }
        if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch { }
        }
    } catch {
        data = null;
    }
    if (data?.code === 'PRICING_CONFIGURATION_UNAVAILABLE') {
        error.code = data.code;
    }
    const message = typeof data === 'string' ? data : data?.message;
    error.friendlyMessage = isPricingUnavailable(error)
        ? `${PRICING_UNAVAILABLE_MESSAGE}${typeof message === 'string' && message.trim() ? ` ${message}` : ''}`
        : typeof message === 'string' && message.trim() && !message.trim().startsWith('<')
            ? message
            : error.response
                ? 'An error occurred. Please try again.'
                : 'Unable to connect to the server. Please check your connection and try again.';
    return error;
};

// Debug logging: active in dev builds or when REACT_APP_DEBUG_API=true at build time
const DEBUG_API = process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_API === 'true';

if (DEBUG_API) {
    api.interceptors.request.use((request) => {
        console.log(
            `[api \u2192] ${String(request.method || 'get').toUpperCase()} ${request.baseURL || ''}${request.url}`
        );
        return request;
    });

    api.interceptors.response.use(
        (response) => {
            console.log(
                `[api \u2190] ${response.status} ${response.config?.url}`
            );
            return response;
        },
        (error) => {
            console.log(
                `[api \u2190 ERROR] ${error.response?.status ?? 'no-response'} ${error.config?.url ?? ''}`,
                error.message
            );
            return Promise.reject(error);
        }
    );
}

// Normalize all error responses so callers can use error.friendlyMessage
api.interceptors.response.use(
    response => response,
    async error => {
        const isCanceled = axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.name === 'AbortError';

        if (!isCanceled) {
            await normalizeApiError(error);
            if (error.config?.data != null) error.config.data = '[redacted]';
            Sentry.captureException(error);
        }

        return Promise.reject(error);
    }
);

const getCalculationData = async () => {
    return api.get(`/calculation/getSimpleCalculationData?property=${PROPERTY_ID}`);
};

const getSolarData = async (data, { signal, calculationMethod } = {}) => {
    const payload = {
        data,
        property: { propertyID: PROPERTY_ID },
    };
    if (calculationMethod) payload.calculationMethod = calculationMethod;
    return api.post('/solar-data/getSolarData', payload, { signal });
};

const sendQuoteEmail = async (data, { signal } = {}) => {
    return api.post('/pdf/getPDFFile', data, { signal });
};

const getAddressDataOptions = async (postCode) => {
    return api.get(`/solar-data/getAddressData?postCode=${encodeURIComponent(postCode)}`);
};

const getFullAddressData = async (id) => {
    const response = await api.get(`/solar-data/getFullAddressData?id=${encodeURIComponent(id)}`);
    if (!response.data?.isSuccess) {
        const error = new Error('Address lookup failed');
        error.friendlyMessage = 'Could not load the selected address. Please enter your address manually.';
        throw error;
    }
    return response;
};

const getCostVariables = async () => {
    return api.get(`/costs/getPublicAddons?propertyID=${PROPERTY_ID}`);
};

const checkServerHealth = async () => {
    return api.get('/health', { timeout: 5000 });
};

const trackJourneyEvent = async (payload, { beacon = false } = {}) => {
    const apiUrl = String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const url = `${apiUrl}/analytics/track-journey`;
    const body = JSON.stringify(payload);
    try {
        if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon
            && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) {
            return { success: true };
        }
        try {
            const response = await api.post('/analytics/track-journey', payload);
            return response.data;
        } catch (error) {
            if (error?.response?.status !== 404) return null;
            const fallbackResponse = await api.post('/v1/analytics/track-journey', payload);
            return fallbackResponse.data;
        }
    } catch (_error) {
        return null;
    }
};

export {
    getCalculationData,
    sendQuoteEmail,
    getSolarData,
    getAddressDataOptions,
    getFullAddressData,
    getCostVariables,
    checkServerHealth,
    trackJourneyEvent,
};
